import type { CSSProperties, MouseEvent, UIEvent } from 'react'
import type { IPopupComponentProps } from './PopupComponent.h'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ButtonIconSizes } from 'atoms/Button/Button.h'
import { InlineCloseButton } from 'atoms/Button/Templates'
import {
  useScrollFocusedIntoViewOnKeyboard,
  useScrolling,
  useVisualViewportRect,
} from 'utils/hooks'
import { COLOR, FIXED_INDENTS, INDENTS, PopupLayout } from 'vars/index'
import { PopupRowGap } from './Popup.h'
import {
  PopupBottomBlockSC,
  PopupComponentSC,
  PopupFooterWrapperSC,
  PopupGradientOverlay,
  PopupHeaderSC,
  PopupOverlayCloseSC,
  PopupOverlaySC,
  PopupScrollableBlockDefault,
  PopupScrollableBlockWithGradient,
  PopupScrolledHeaderSC,
  PopupTopBlockSC,
} from './PopupComponent.styled'
import { resolvePopupScrollFades } from './popupScrollFades'

export const PopupComponent = ({
  overlayColor = COLOR.SURFACE_OPACITY_DARK_70,
  overlayGradient,
  withCloseButton = true,

  popupBackgroundColor = COLOR.SURFACE_DARK_4,
  popupBackgroundGradient,
  horizontalPadding = PopupLayout.horizontalPaddings,
  headerBottomPadding = PopupLayout.headerBottomPadding,
  footerPaddingTop = FIXED_INDENTS.INDENT_40,

  onClosePopup,

  children,

  withGradientScrollableBlock,
  isShoppingCartPopup,
  isContentDetailsPopup,
  width,
  skipKeyboardScrollIntoView,
  skipKeyboardOverlayReposition,
}: IPopupComponentProps) => {
  const header = children?.[0]
  const body = children?.[1]
  const footer = children?.[2]

  // The content details panel reads as one sheet: its title travels with the
  // sections instead of staying pinned while they scroll underneath.
  const headerScrollsWithBody = Boolean(isContentDetailsPopup && header)

  const popupRootRef = useRef<HTMLDivElement>(null)
  const popupBodyRef = useRef<HTMLDivElement>(null)
  const closeOverlayRef = useRef<HTMLDivElement>(null)
  const { rect, isKeyboardLikelyOpen } = useVisualViewportRect()

  const overlayStyle = useMemo<CSSProperties | undefined>(
    () =>
      isKeyboardLikelyOpen && !skipKeyboardOverlayReposition
        ? {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            bottom: 'auto',
            right: 'auto',
          }
        : undefined,
    [
      isKeyboardLikelyOpen,
      skipKeyboardOverlayReposition,
      rect.height,
      rect.left,
      rect.top,
      rect.width,
    ]
  )

  const [isTopGradientShown, setIsTopGradientShown] = useState(false)
  const [isBottomGradientShown, setIsBottomGradientShown] = useState(false)

  const syncGradients = useCallback((element: HTMLDivElement) => {
    const fades = resolvePopupScrollFades({
      scrollTop: element.scrollTop,
      scrollHeight: element.scrollHeight,
      clientHeight: element.clientHeight,
    })
    setIsTopGradientShown(fades.isTopGradientShown)
    setIsBottomGradientShown(fades.isBottomGradientShown)
  }, [])

  const handleScroll = (e: UIEvent<HTMLDivElement>) =>
    syncGradients(e.currentTarget)

  // Overflowing content must show the bottom fade on open, not only after the
  // first scroll. ResizeObserver covers flex layout that settles after mount.
  useEffect(() => {
    const element = popupBodyRef.current
    if (!element) return

    syncGradients(element)
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => syncGradients(element))
    observer.observe(element)
    return () => observer.disconnect()
  }, [syncGradients, children])

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation()
    if (onClosePopup) onClosePopup()
  }

  const handleEscButton = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClosePopup) onClosePopup()
    },
    [onClosePopup]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscButton)
    return () => document.removeEventListener('keydown', handleEscButton)
  }, [handleEscButton])

  // The element that opened the popup must get the focus back when the popup
  // goes away, whichever way it was dismissed (close button, Escape, overlay).
  useEffect(() => {
    const trigger = document.activeElement
    return () => {
      if (trigger instanceof HTMLElement) trigger.focus()
    }
  }, [])

  useScrollFocusedIntoViewOnKeyboard(
    popupRootRef,
    isKeyboardLikelyOpen && !skipKeyboardScrollIntoView,
    rect.height
  )

  useScrolling(closeOverlayRef)

  return (
    <PopupOverlaySC
      overlayColor={overlayColor}
      overlayGradient={overlayGradient}
      style={overlayStyle}
    >
      <PopupOverlayCloseSC ref={closeOverlayRef} onClick={handleClose} />
      <PopupComponentSC
        ref={popupRootRef}
        backgroundColor={popupBackgroundColor}
        backgroundGradient={popupBackgroundGradient}
        paddingInline={horizontalPadding}
        width={width}
        isShoppingCartPopup={isShoppingCartPopup}
        isKeyboardLikelyOpen={isKeyboardLikelyOpen}
        isContentDetailsPopup={isContentDetailsPopup}
      >
        <PopupTopBlockSC isShoppingCartPopup={isShoppingCartPopup}>
          {Boolean(withCloseButton) &&
            // The content details panel is sized in fixed pixels, so its close
            // button must not follow the em-based type scale-up above 1640.
            (isContentDetailsPopup ? (
              <InlineCloseButton
                onClick={handleClose}
                paddings={[FIXED_INDENTS.INDENT_40]}
                iconSize={ButtonIconSizes.xSm}
              />
            ) : (
              <InlineCloseButton
                onClick={handleClose}
                paddings={[INDENTS.INDENT_40]}
              />
            ))}
        </PopupTopBlockSC>
        {header && !headerScrollsWithBody && (
          <PopupHeaderSC paddingsHeaderBottom={headerBottomPadding}>
            {header}
          </PopupHeaderSC>
        )}
        {Boolean(body) &&
          (withGradientScrollableBlock ? (
            <PopupGradientOverlay
              data-top-fade={isTopGradientShown ? 'true' : 'false'}
              data-bottom-fade={isBottomGradientShown ? 'true' : 'false'}
              data-content-details={isContentDetailsPopup ? 'true' : 'false'}
            >
              <PopupScrollableBlockWithGradient
                ref={popupBodyRef}
                onScroll={handleScroll}
              >
                {headerScrollsWithBody && (
                  <PopupScrolledHeaderSC
                    paddingsHeaderBottom={headerBottomPadding}
                  >
                    {header}
                  </PopupScrolledHeaderSC>
                )}
                {body}
              </PopupScrollableBlockWithGradient>
            </PopupGradientOverlay>
          ) : (
            <PopupScrollableBlockDefault
              ref={popupBodyRef}
              rowGapDescription={PopupRowGap.INDENT_0}
            >
              {body}
            </PopupScrollableBlockDefault>
          ))}
        {footer && (
          <PopupFooterWrapperSC paddingTop={footerPaddingTop}>
            {footer}
          </PopupFooterWrapperSC>
        )}
        <PopupBottomBlockSC />
      </PopupComponentSC>
    </PopupOverlaySC>
  )
}
